import React, { useEffect, useRef, useState } from "react";
import Scanner from "./Scanner";
import PressureData from "./PressureData";
import { CV_32F, Mat, resize } from "@techstark/opencv-js";
import PressureViewerComponent, { PressureViewerHandler } from "./PressureViewer";
import FrameProcessorPressure from "components/Modals/PatientModal/utils/FrameProcessorPressure";
import toast from "react-hot-toast";
import { CircularProgress, Box, ImageList, ImageListItem, ImageListItemBar, IconButton } from '@mui/material';
import cv from "@techstark/opencv-js";
import { getPressureCalib } from "services/designService";
import { useApi } from "context/ApiProvider";
import { useAuth } from "context/AuthContext";
import { ZoneNames } from "components/Modals/PatientModal/utils/zoneProcessor";
import DeleteIcon from '@mui/icons-material/Delete';
import CopyToRightIcon from '@mui/icons-material/ArrowForward';
import CopyToLeftIcon from '@mui/icons-material/ArrowBack';
import { useDesignStore } from "context/DesignContext";
import { showAlertDialog } from "components/Dialog/dialog";
import { CameraOutlined, PlayArrowOutlined, SaveOutlined, StopOutlined } from "@mui/icons-material";
import {
    VOICE_PRESSURE_SCAN_DONE_EVENT,
    VOICE_PRESSURE_SCAN_EVENT,
    VOICE_PRESSURE_SCAN_READY_EVENT,
    VOICE_SPEAK_LINE_EVENT,
} from "Voice/voiceCommands";
import { voiceText } from "Voice/voiceLanguage";

interface NewScanProps {
    isDarkMode: boolean;
    onAccept: () => void;
    onClose: () => void;
    triggerSave?: boolean;
    resetSaveTrigger: () => void;
}

const NewScan: React.FC<NewScanProps> = ({
    isDarkMode,
    onAccept,
    onClose,
    triggerSave, 
    resetSaveTrigger
}) => {
    const [loading, setLoading] = useState<boolean>(true);
    const [portPicker, setPortPicker] = useState(false);
    const [hovered, setHoverd] = useState<'static' | 'dynamic' | 'accept' | 'capture' | 'right' | 'left' | 'stop' | null>(null);
    const [scanMode, setScanMode] = useState<'static' | 'dynamic'>('static');
    const scanModeRef = useRef<'static' | 'dynamic'>('static');
    scanModeRef.current = scanMode;
    const [captureDynamic, setCaptureDynamic] = useState<boolean>(false);
    const [dynamicTest, setDynamicTest] = useState<'right' | 'left' | 'auto' | 'none'>('none');
    const [newScannerData, setNewScannerData] = useState<Mat | undefined>(undefined);
    const [staticCaptureData, setStaticCaptureData] = useState<Mat | undefined>(undefined);
    const staticCaptureRef = useRef<Mat | undefined>(undefined);
    staticCaptureRef.current = staticCaptureData;
    const [rightDynamicData, setRightDynamicData] = useState<Mat | undefined>(undefined);
    const [leftDynamicData, setLeftDynamicData] = useState<Mat | undefined>(undefined);
    const [frameRate, setFrameRate] = useState<number>(0);
    const [sumFrame, setSumFrame] = useState<number>(0);
    const [savedFrame, setSavedFrame] = useState<number>(0);
    const [isAcceptEnabled, setIsAcceptEnabled] = useState<boolean>(false);

    const [leftGallery, setLeftGallery] = useState<string[]>([]);
    const [rightGallery, setRightGallery] = useState<string[]>([]);

    const api = useApi();
    const { user } = useAuth();

    const pressureViewerRef = useRef<PressureViewerHandler>(null);

    const captureDynamicRef = useRef<boolean>(captureDynamic);
    const dynamicTestRef = useRef<'right' | 'left' | 'auto' | 'none'>('none');

    const connectionStatus = useRef<boolean>(false);
    const connectReasonRef = useRef<"ok" | "noCalibration" | "needsPort" | "noSerial">("ok");
    const unplugNotifiedRef = useRef(false);
    const onCloseRef = useRef(onClose);
    onCloseRef.current = onClose;
    const scannerRef = useRef<Scanner | null>(null);
    const intervalIdRef = useRef<NodeJS.Timeout | null>(null);
    const frameProcessor = useRef<FrameProcessorPressure>(new FrameProcessorPressure());

    const footPressed = useRef<boolean>(false);
    const stopTimer = useRef<number>(0);

    const leftFootDynamicPressureDataList = useRef<PressureData[]>([]);
    const rightFootDynamicPressureDataList = useRef<PressureData[]>([]);

    useEffect(() => {
        const scanner = new Scanner();
        scannerRef.current = scanner;
        scanner.setOnUnplugged(() => {
            if (unplugNotifiedRef.current) return;
            unplugNotifiedRef.current = true;
            connectionStatus.current = false;
            stopDataLoop();
            toast.error("The pressure scanner was disconnected.", { duration: 6000 });
            document.dispatchEvent(new CustomEvent(VOICE_SPEAK_LINE_EVENT, {
                detail: {
                    text: voiceText({
                        en: "The pressure scanner was disconnected.",
                        nl: "De drukscanner is losgekoppeld.",
                        fa: "دستگاه اسکنر جدا شده.",
                        zh: "压力扫描仪已断开。",
                    }),
                },
            }));
            onCloseRef.current();
        });

        const init = async () => {
            try {
                setLoading(true);
                await connect();
                if (connectionStatus.current) startDataLoop();
            } catch (err) {
                console.error("Error initializing:", err);
            } finally {
                const reason = connectReasonRef.current;
                document.dispatchEvent(new CustomEvent(VOICE_PRESSURE_SCAN_READY_EVENT, {
                    detail: {
                        connected: connectionStatus.current,
                        portPicker: reason === "needsPort",
                        noCalibration: reason === "noCalibration",
                        noSerial: reason === "noSerial",
                        mode: "static",
                        capturing: false,
                        hasStaticCapture: false,
                        dynamicLeft: 0,
                        dynamicRight: 0,
                        canSave: false,
                        message: reason === "ok"
                            ? "Pressure scanner is connected. Ask whether they want a static or a dynamic test."
                            : reason === "noCalibration"
                            ? "Pressure is not calibrated. Tell the user they must add a pressure calibration first. Do not continue the pressure test."
                            : reason === "noSerial"
                            ? "This browser cannot open the serial port. Tell them to use Chrome or Edge."
                            : "The pressure device is not connected. Tell the user to plug in the scanner, then click Connect scanner in the panel and choose the USB port. Voice cannot open that Chrome picker. After they connect, call setPressureScan action=status.",
                    }
                }));
            }
        };
        init();


        return () => {
            setLoading(false);
            stopDataLoop();
            scanner.Disconnect();
            try {
                leftDynamicData?.delete();
                rightDynamicData?.delete();
                staticCaptureData?.delete();
                newScannerData?.delete();
            } catch (error) {
                console.error("Error cleaning up resources:", error);
            }
        };
    }, []);

    useEffect(() => {
        if (scannerRef.current?.isInitialized) {
            setLoading(false);
        }
    }, [scannerRef.current?.isInitialized]);

    useEffect(() => {
        captureDynamicRef.current = captureDynamic;
        dynamicTestRef.current = dynamicTest;
    }, [captureDynamic]);

    let isProcessing = false;

    const startDataLoop = () => {
        intervalIdRef.current = setInterval(async () => {
            const imgData = scannerRef.current?.GetLastFrame()?.clone();
            if (!imgData || imgData === undefined) return;

            setNewScannerData(imgData);

            setFrameRate(scannerRef.current?.getFrameRate() ?? 0);
            setSavedFrame(scannerRef.current?.getSavedCount() ?? 0);
            setSumFrame(frameProcessor.current.getSumOfFrame(imgData));

            if (captureDynamicRef.current) {
                const elapsed = scannerRef.current?.getElapsedms();

                // // add a progressbar to viewer and update its value based on elapsed time
                // const progressBar = document.getElementById("progress-bar");
                // if (progressBar) {
                //     progressBar.style.width = `${Math.min((elapsed / 1000) * 100, 100)}%`;
                // }

                if (frameProcessor.current?.getSumOfFrame(imgData) > 500) {
                    footPressed.current = true;
                    stopTimer.current = 0;
                }
                else {
                    stopTimer.current++;
                }

                if (footPressed.current && stopTimer.current > 10) {
                    startCapture(true);
                }

                if (scannerRef.current?.isArrayReady) {
                    if (isProcessing) return;

                    let calibFrames = scannerRef.current.getCopyOfSavedFrames();
                    let calibMaxFrame = frameProcessor.current.getMaxFrame(calibFrames);

                    let trimEnds = frameProcessor.current.getTrimEnds(calibFrames);
                    calibFrames = calibFrames.slice(trimEnds.start, trimEnds.end + 1);

                    if (calibFrames.length < 4) return;
                    if (calibMaxFrame === null) return;

                    const needRotate = frameProcessor.current.isRotated(calibFrames);

                    if (needRotate) {
                        calibFrames = frameProcessor.current.rotate180List(calibFrames);
                        calibMaxFrame = frameProcessor.current.rotate180(calibMaxFrame);
                    }

                    let trimedFrameTime = scannerRef.current.getCopyOfSavedFramesTimes().slice(trimEnds.start, trimEnds.end + 1);
                    const firstRoom = trimedFrameTime[0];

                    for (let i = 0; i < trimedFrameTime.length; i++) {
                        trimedFrameTime[i] -= firstRoom;
                    }

                    if (dynamicTestRef.current === 'right') {
                        setRightDynamicData(calibMaxFrame);

                        const pd = new PressureData();
                        pd.CalibFrames_Right = calibFrames;
                        pd.FrameTimes_Right = trimedFrameTime;
                        pd.CalibMaxFrame_Right = calibMaxFrame;
                        pd.ExamType = 'dynamic';

                        rightFootDynamicPressureDataList.current.push(pd);
                        setCaptureDynamic(false);
                    }
                    else if (dynamicTestRef.current === 'left') {
                        setLeftDynamicData(calibMaxFrame);

                        const pd = new PressureData();
                        pd.CalibFrames_Left = calibFrames;
                        pd.FrameTimes_Left = trimedFrameTime;
                        pd.CalibMaxFrame_Left = calibMaxFrame;
                        pd.ExamType = 'dynamic';

                        leftFootDynamicPressureDataList.current.push(pd);
                        setCaptureDynamic(false);
                    }
                    else if (dynamicTestRef.current === 'auto') {
                        // const isLeftFoot = await isCaptureFrameLeftFoot(rawMaxFrame);

                        isProcessing = true;

                        pressureViewerRef.current?.setCalibMaxFrame(calibMaxFrame);
                        const blob = await matToBlobWithCanvas(calibMaxFrame, 'image/png');

                        const formData = new FormData();
                        formData.append('footStep', blob, 'footstep.png');

                        const detect = await api.post(`/api/designs/pressure/footDetector`, formData);
                        let isLeftFoot = false;
                        if (detect.ok && typeof detect.body === 'boolean') {
                            isLeftFoot = detect.body;
                        } else {
                            try {
                                isLeftFoot = await isCaptureFrameLeftFoot(calibMaxFrame);
                            } catch (detectError) {
                                console.error('Foot side detection failed', detect.body ?? detectError);
                                isProcessing = false;
                                return;
                            }
                        }

                        if (isLeftFoot) {
                            const pd = new PressureData();
                            pd.CalibFrames_Left = calibFrames;
                            pd.FrameTimes_Left = trimedFrameTime;
                            pd.CalibMaxFrame_Left = calibMaxFrame;
                            pd.ExamType = 'dynamic';

                            leftFootDynamicPressureDataList.current.push(pd);

                            pressureViewerRef.current?.getPrintBitmap(88, 104, 'Dynamic_Auto').then(cnv => {
                                const newItem = matToDataURL(cnv);
                                setLeftGallery(prv => prv.includes(newItem) ? prv : [...prv, newItem]);
                                return newItem;
                            });
                        } else {
                            const pd = new PressureData();
                            pd.CalibFrames_Right = calibFrames;
                            pd.FrameTimes_Right = trimedFrameTime;
                            pd.CalibMaxFrame_Right = calibMaxFrame;
                            pd.ExamType = 'dynamic';

                            rightFootDynamicPressureDataList.current.push(pd);

                            pressureViewerRef.current?.getPrintBitmap(88, 104, 'Dynamic_Auto').then(cnv => {
                                const newItem = matToDataURL(cnv);
                                setRightGallery(prv => prv.includes(newItem) ? prv : [...prv, newItem]);
                            });
                        }

                        if (rightFootDynamicPressureDataList.current.length > 0 && leftFootDynamicPressureDataList.current.length > 0)
                            setIsAcceptEnabled(true);
                        startCapture(false);
                        isProcessing = false;
                    }

                    // setCaptureDynamic(false);
                }
            }

            // imgData.delete();
        }, 100);
    };

    const matToDataURL = (cnvs: any) => {
        // const canvas = document.createElement('canvas');
        // cv.imshow(canvas, mat);
        const dataUrl = cnvs.toDataURL('image/png');
        cnvs.remove();
        return dataUrl;
    };

    function matToBlobWithCanvas(mat32S: cv.Mat, mimeType = 'image/png') {
        return new Promise((resolve, reject) => {

            // 1. Mat Preparation: Convert CV_32S (int) to CV_8U (byte) for display/encoding
            // This is necessary because canvas only handles 8-bit data.
            const mat8U = new cv.Mat();
            // Scaling is important if your 32S data exceeds 0-255.
            mat32S.convertTo(mat8U, cv.CV_8U, 1.0, 0.0);

            // 2. Create and Display on Canvas
            const canvas = document.createElement('canvas');
            try {
                cv.imshow(canvas, mat8U); // Renders the Mat onto the canvas
            } catch (e) {
                reject(new Error("cv.imshow failed: Mat may have wrong depth or channels."));
                return;
            }

            // 3. Convert Canvas to Blob (standard file format)
            // This process handles the compression and file structuring (e.g., PNG headers).
            canvas.toBlob((blob) => {
                mat8U.delete(); // Clean up the temporary Mat
                canvas.remove(); // Clean up the temporary canvas element
                if (blob) {
                    resolve(blob);
                } else {
                    reject(new Error("Canvas failed to create Blob."));
                }
            }, mimeType);
        });
    }

    function extendROI(inc: number, roi: cv.Rect, size: cv.Size) {
        roi.x -= inc;
        roi.y -= inc;
        roi.width += 2 * inc;
        roi.height += 2 * inc;

        // Ensure ROI stays within image bounds
        roi.x = Math.max(0, roi.x);
        roi.y = Math.max(0, roi.y);
        roi.width = Math.min(size.width, roi.width + roi.x) - roi.x;
        roi.height = Math.min(size.height, roi.height + roi.y) - roi.y;

        return roi;
    }

    async function isCaptureFrameLeftFoot(frame: Mat): Promise<boolean> {
        const fixedImagePath = './Settings/AutoDynamic/autoDynamicTemplate.png';
        // const movingImagePath = './Settings/AutoDynamic/movingImage.png';
        // const transformedImagePath = 'sth';

        // Clone frame
        let calibMaxFrame = frame.clone();

        // Save frame (in Node, use fs + cv.imencode; in browser this is different)
        // cv.imwrite(movingImagePath, calibMaxFrame);

        // Transform frame (you’ll need to implement or wrap your own registration)

        const [fixedImg] = await Promise.all([
            loadImage(fixedImagePath)
        ]);

        const transform = await euler2DTransform(frame, fixedImg);

        let transformedFrame = transformGrayImage(calibMaxFrame, transform);


        calibMaxFrame.delete();

        // Crop ROI (custom function required)
        const roi = frameProcessor.current.autoCropROIJustUpperMargin(transformedFrame);

        let croppedMaxFrame = transformedFrame.roi(roi);

        transformedFrame.delete();

        // Resize by factor of 10
        const resizedFrame = new cv.Mat();
        cv.resize(
            croppedMaxFrame,
            resizedFrame,
            new cv.Size(croppedMaxFrame.cols * 10, croppedMaxFrame.rows * 10),
            0,
            0,
            cv.INTER_LINEAR
        );
        croppedMaxFrame.delete();

        const frameWidth = resizedFrame.cols;
        const frameHeight = resizedFrame.rows;

        let leftSideminY = frameHeight / 2;

        // Scan left third
        for (let i = 0; i < frameWidth / 3; i++) {
            for (let j = 0; j < frameHeight / 2; j++) {
                const pixel = resizedFrame.ucharPtr(j, i);
                const r = pixel[0]; // Gray image, but if RGB, adjust
                const g = pixel[1] ?? r;
                const b = pixel[2] ?? r;

                if (r < 20 && g < 20 && b < 20) {
                    continue;
                } else if (j < leftSideminY) {
                    leftSideminY = j;
                    break;
                }
            }
        }

        let rightSideminY = frameHeight / 2;

        // Scan right third
        for (let i = Math.floor((frameWidth * 2) / 3); i < frameWidth; i++) {
            for (let j = 0; j < frameHeight / 2; j++) {
                const pixel = resizedFrame.ucharPtr(j, i);
                const r = pixel[0];
                const g = pixel[1] ?? r;
                const b = pixel[2] ?? r;

                if (r < 20 && g < 20 && b < 20) {
                    continue;
                } else if (j < rightSideminY) {
                    rightSideminY = j;
                    break;
                }
            }
        }

        resizedFrame.delete();

        return rightSideminY < leftSideminY;
    }

    function euler2DTransform(
        movingImageMat: Mat, fixedImageElement: HTMLImageElement
    ): Promise<Mat> {
        return new Promise((resolve, reject) => {
            try {
                // Convert images to cv.Mat
                const fixedSrc = cv.imread(fixedImageElement);
                const movingSrc = movingImageMat.clone();
                console.log('Fixed Src: ', fixedSrc);
                console.log('Moving Src: ', movingSrc);

                // Convert to grayscale
                const fixedGray = new Mat();
                const movingGray = new Mat();
                cv.cvtColor(fixedSrc, fixedGray, cv.COLOR_RGBA2GRAY);
                console.log('Fixed Gray: ', fixedGray);

                if (movingSrc.channels() === 4) {
                    cv.cvtColor(movingSrc, movingGray, cv.COLOR_RGBA2GRAY);
                } else if (movingSrc.channels() === 3) {
                    cv.cvtColor(movingSrc, movingGray, cv.COLOR_RGB2GRAY);
                } else {
                    movingSrc.copyTo(movingGray);
                }

                if (movingGray.rows !== fixedGray.rows || movingGray.cols !== fixedGray.cols) {
                    resize(movingGray, movingGray, fixedGray.size());
                    console.log("Need resizing");
                }

                fixedGray.convertTo(fixedGray, CV_32F, 1.0 / 255.0);;
                movingGray.convertTo(movingGray, CV_32F, 1.0 / 255.0);;


                // Initialize warp matrix (2x3) for Euclidean transform
                let warpMatrix = Mat.eye(2, 3, cv.CV_32F);
                console.log('warpMat: ', warpMatrix);

                // Define criteria for ECC algorithm (max iterations, epsilon)
                const criteria = new cv.TermCriteria(cv.TermCriteria_COUNT + cv.TermCriteria_EPS, 50, 1e-4);
                console.log('criteria: ', criteria)
                // Run ECC algorithm to find optimal transform

                const noArray = new cv.Mat();
                const cc = cv.findTransformECC(
                    fixedGray,
                    movingSrc,
                    warpMatrix,
                    cv.MOTION_EUCLIDEAN,
                    criteria,
                    noArray,
                    5
                );
                console.log('pass cv.findTransformECC: ', cc);

                // Clean up intermediate mats
                fixedSrc.delete();
                movingSrc.delete();
                fixedGray.delete();
                movingGray.delete();
                noArray.delete();

                // warpMatrix is the resulting 2x3 affine matrix: rotation + translation
                resolve(warpMatrix);
            } catch (err) {
                reject(err);
            }
        });
    }

    function transformGrayImage(
        imageMat: Mat,
        transformMatrix: number[] | Mat
    ): Mat {
        let warpMat: Mat;

        if (transformMatrix instanceof cv.Mat) {
            warpMat = transformMatrix;
        } else {
            // Assuming transformMatrix is a flat array of length 6:
            warpMat = cv.matFromArray(2, 3, cv.CV_32F, transformMatrix);
        }

        const dst = new cv.Mat();
        const dsize = new cv.Size(imageMat.cols, imageMat.rows);

        cv.warpAffine(
            imageMat,
            dst,
            warpMat,
            dsize,
            cv.INTER_LINEAR,
            cv.BORDER_CONSTANT,
            new cv.Scalar(0, 0, 0, 255) // black border fill
        );

        if (!(transformMatrix instanceof cv.Mat)) {
            warpMat.delete();
        }

        return dst;
    }

    function loadImage(url: string): Promise<HTMLImageElement> {
        return new Promise((resolve, reject) => {
            const img = new Image();
            img.crossOrigin = "anonymous";  // If loading from external server, for CORS
            img.onload = () => resolve(img);
            img.onerror = (e) => reject(e);
            img.src = url;
        });
    }

    const stopDataLoop = () => {
        if (intervalIdRef.current) {
            clearInterval(intervalIdRef.current);
            intervalIdRef.current = null;
        }
    };

    const handleCapture = (): { ok: boolean; message: string } => {
        const imgData = scannerRef.current?.GetLastFrame();
        if (!imgData) {
            return { ok: false, message: "No live scanner frame. Ask them to wait a moment, then say capture again." };
        }

        setIsAcceptEnabled(true);
        staticCaptureRef.current = imgData;
        setStaticCaptureData(imgData);
        if (frameProcessor.current.getSumOfFrame(imgData) < 500) {
            return { ok: false, message: "Capture taken, but no foot was detected. Ask them to stand on the sensor, then say capture again." };
        }
        return { ok: true, message: "Static capture taken. Ask if the capture looks correct. If they confirm, call setPressureScan action=save." };
    };

    const StaticPressureLengthConst = 10;
    const DynamicPresureLengthConst = -5;

    const handleAccept = async (): Promise<{ ok: boolean; message: string }> => {
        resetSaveTrigger();
        if (scanModeRef.current === 'static') {
            const staticCaptureData = staticCaptureRef.current;
            if (staticCaptureData === undefined) {
                toast.error('No frame has been captured. Please capture a frame before saving.', { duration: 10000 });
                return { ok: false, message: "No frame has been captured. Ask them to stand on the sensor and say capture first." };
            }

            if (frameProcessor.current.getSumOfFrame(staticCaptureData) < 500) {
                toast.error('No Foot detected! \nPlease stand on the sensor \nand after seeing the soles, press capture!', { duration: 10000 });
                return { ok: false, message: "No foot detected. Ask them to stand on the sensor, say capture, then save." };
            }

            setLoading(true);
            // let footAlign = new FootAligner(cv);
            const pData = new PressureData();
            pData.ExamType = scanModeRef.current;
            pData.status = 'done';

            pData.ROI_Left = frameProcessor.current.autoCropROILeft(staticCaptureData);
            pData.FrameTimes_Left = [0];
            pData.CalibFrames_Left = [staticCaptureData];
            pData.CalibMaxFrame_Left = staticCaptureData;
            // pData.LeftControlPoints = await footAlign.process(pData.CalibMaxFrame_Left.clone().roi({
            //     x: 0, y: 0,
            //     width: pData.ROI_Left.width + pData.ROI_Left.x,
            //     height: staticCaptureData.rows
            // }), 'left', 'static');
            // // pData.LeftControlPoints = pData.LeftControlPoints.map(point => ({
            // //     x: point.x + pData.ROI_Left.x,
            // //     y: point.y
            // // }));
            // pData.Axis_Left = new Line(pData.LeftControlPoints[1], pData.LeftControlPoints[19]);
            // pData.WidthLine_Left = new Line(pData.LeftControlPoints[3], pData.LeftControlPoints[8]);
            // pData.HeelLine_Left = new Line(pData.LeftControlPoints[18], pData.LeftControlPoints[20]);

            pData.COPs_Left = frameProcessor.current.GetCOPArray(pData.CalibFrames_Left, pData.ROI_Left);

            const leftROI = pData.ROI_Left;
            const left_TOPROI = new cv.Rect(leftROI.x, leftROI.y, leftROI.width, leftROI.height / 3.0);
            const left_BottomROI = new cv.Rect(leftROI.x, leftROI.y + (leftROI.height * 0.66), leftROI.width, leftROI.height / 3.0);

            var HelperCOPs_Left: cv.Point[] = [];
            HelperCOPs_Left.push(frameProcessor.current.GetCOPArray(pData.CalibFrames_Left, left_BottomROI)[0]);
            HelperCOPs_Left.push(frameProcessor.current.GetCOPArray(pData.CalibFrames_Left, left_TOPROI)[0]);

            pData.Axis_Left = frameProcessor.current.getAxisLine(HelperCOPs_Left, pData.ROI_Left, pData.CalibMaxFrame_Left);
            pData.WidthLine_Left = frameProcessor.current.getWidthLine(pData.Axis_Left, pData.ROI_Left, pData.CalibMaxFrame_Left);
            pData.HeelLine_Left = frameProcessor.current.GetHeelLine(pData.Axis_Left, pData.ROI_Left, pData.CalibMaxFrame_Left);

            pData.Zones_Left = frameProcessor.current.GetZones(pData.Axis_Left, pData.WidthLine_Left, pData.HeelLine_Left, pData.ROI_Left, "Left");


            pData.ROI_Right = frameProcessor.current.autoCropROIRight(staticCaptureData);
            pData.FrameTimes_Right = [0];
            pData.CalibFrames_Right = [staticCaptureData];
            pData.CalibMaxFrame_Right = staticCaptureData;
            // pData.RightControlPoints = await footAlign.process(pData.CalibMaxFrame_Right.clone().roi({
            //     ...pData.ROI_Right,
            //     // x: pData.ROI_Right.x,
            //     y: 0,
            //     // width: staticCaptureData.cols - pData.ROI_Right.x,
            //     height: staticCaptureData.rows
            // }), 'right', 'static');
            // pData.RightControlPoints = pData.RightControlPoints.map(point => ({
            //     x: point.x + pData.ROI_Right.x,
            //     y: point.y
            // }));
            // pData.Axis_Right = new Line(pData.RightControlPoints[1], pData.RightControlPoints[19]);
            // pData.WidthLine_Right = new Line(pData.RightControlPoints[3], pData.RightControlPoints[8]);
            // pData.HeelLine_Right = new Line(pData.RightControlPoints[18], pData.RightControlPoints[20]);

            pData.COPs_Right = frameProcessor.current.GetCOPArray(pData.CalibFrames_Right, pData.ROI_Right);

            const rightROI = pData.ROI_Right;
            const right_TOPROI = new cv.Rect(rightROI.x, rightROI.y, rightROI.width, rightROI.height / 3.0);
            const right_BottomROI = new cv.Rect(rightROI.x, rightROI.y + (rightROI.height * 0.66), rightROI.width, rightROI.height / 3.0);

            var HelperCOPs_Right: cv.Point[] = [];
            HelperCOPs_Right.push(frameProcessor.current.GetCOPArray(pData.CalibFrames_Right, right_BottomROI)[0]);
            HelperCOPs_Right.push(frameProcessor.current.GetCOPArray(pData.CalibFrames_Right, right_TOPROI)[0]);

            pData.Axis_Right = frameProcessor.current.getAxisLine(HelperCOPs_Right, pData.ROI_Right, pData.CalibMaxFrame_Right);
            pData.WidthLine_Right = frameProcessor.current.getWidthLine(pData.Axis_Right, pData.ROI_Right, pData.CalibMaxFrame_Right);
            pData.HeelLine_Right = frameProcessor.current.GetHeelLine(pData.Axis_Right, pData.ROI_Right, pData.CalibMaxFrame_Right);

            pData.Zones_Right = frameProcessor.current.GetZones(pData.Axis_Right, pData.WidthLine_Right, pData.HeelLine_Right, pData.ROI_Right, "Right");

            const length_Right = Math.round(frameProcessor.current.getRealDistance(pData.Axis_Right.start, pData?.Axis_Right.end));
            const length_Left = Math.round(frameProcessor.current.getRealDistance(pData.Axis_Left.start, pData?.Axis_Left.end));
            pData.FootLength_Left = pData.FootLength_Right = Math.round((length_Left + length_Right) / 2) + StaticPressureLengthConst;

            var zM1R = pData.Zones_Right.GetZoneCenteroid(ZoneNames.M1);
            var zM5R = pData.Zones_Right.GetZoneCenteroid(ZoneNames.M5);

            const zerox = pData.ROI_Right.x + pData.ROI_Right.width;
            const zeroy = pData.ROI_Right.y;

            const M1R = new cv.Point((zerox - zM1R.x) * pData.PressureSensorWidth + pData.PressureSensorWidth / 2.0, (zM1R.y - zeroy) * pData.PressureSensorHeight + pData.PressureSensorHeight / 2.0);
            const M5R = new cv.Point((zerox - zM5R.x) * pData.PressureSensorWidth + pData.PressureSensorWidth / 2.0, (zM5R.y - zeroy) * pData.PressureSensorHeight + pData.PressureSensorHeight / 2.0);

            const metCenterR = new cv.Point((M1R.x + M5R.x) / 2, (M1R.y + M5R.y) / 2);

            const ZoneSensorsR = pData.Zones_Right?.GetZoneSensors(ZoneNames.HL, pData.ROI_Right);
            if (ZoneSensorsR) {
                const hmSensorsR = pData.Zones_Right.GetZoneSensors(ZoneNames.HM, pData.ROI_Right);
                if (hmSensorsR) ZoneSensorsR.push(...hmSensorsR);
            }
            const hR = frameProcessor.current?.GetCenterOfPressure(ZoneSensorsR, pData.CalibMaxFrame_Right, 0.5);
            const heelCenterR = new cv.Point((zerox - hR.x) * pData.PressureSensorWidth + pData.PressureSensorWidth / 2.0, (hR.y - zeroy) * pData.PressureSensorHeight + pData.PressureSensorHeight / 2.0);

            var zM1L = pData.Zones_Left.GetZoneCenteroid(ZoneNames.M1);
            var zM5L = pData.Zones_Left.GetZoneCenteroid(ZoneNames.M5);

            const zeroxL = pData.ROI_Left.x + pData.ROI_Left.width;
            const zeroyL = pData.ROI_Left.y;

            const M1L = new cv.Point((zeroxL - zM1L.x) * pData.PressureSensorWidth + pData.PressureSensorWidth / 2.0, (zM1L.y - zeroyL) * pData.PressureSensorHeight + pData.PressureSensorHeight / 2.0);
            const M5L = new cv.Point((zeroxL - zM5L.x) * pData.PressureSensorWidth + pData.PressureSensorWidth / 2.0, (zM5L.y - zeroyL) * pData.PressureSensorHeight + pData.PressureSensorHeight / 2.0);

            const metCenterL = new cv.Point((M1L.x + M5L.x) / 2, (M1L.y + M5L.y) / 2);

            const ZoneSensorsL = pData.Zones_Left?.GetZoneSensors(ZoneNames.HL, pData.ROI_Left);
            if (ZoneSensorsL) {
                const hmSensorsL = pData.Zones_Left.GetZoneSensors(ZoneNames.HM, pData.ROI_Left);
                if (hmSensorsL) ZoneSensorsL.push(...hmSensorsL);
            }
            const hL = frameProcessor.current?.GetCenterOfPressure(ZoneSensorsL, pData.CalibMaxFrame_Left, 0.5);
            const heelCenterL = new cv.Point((zeroxL - hL.x) * pData.PressureSensorWidth + pData.PressureSensorWidth / 2.0, (hL.y - zeroyL) * pData.PressureSensorHeight + pData.PressureSensorHeight / 2.0);

            pData.MetCenter = [metCenterR, metCenterL];
            pData.HeelCenter = [heelCenterR, heelCenterL];

            await pData.Save();

            const event = new CustomEvent('newPressureScan', { detail: { pressureData: pData } });
            document.dispatchEvent(event);
            onAccept();
            return { ok: true, message: "Static pressure scan saved." };
        }
        if (scanModeRef.current === 'dynamic') {
            if (leftFootDynamicPressureDataList.current.length === 0) {
                toast.error('No data for the left foot. Please perform the left foot capture.', { duration: 10000 });
                return { ok: false, message: "No left-foot dynamic data yet. Ask them to walk again, then confirm foot direction before saving." };
            }
            if (rightFootDynamicPressureDataList.current.length === 0) {
                toast.error('No data for the right foot. Please perform the right foot capture.', { duration: 10000 });
                return { ok: false, message: "No right-foot dynamic data yet. Ask them to walk again, then confirm foot direction before saving." };
            }

            const pDataList = [];
            const pDataLength = Math.max(leftFootDynamicPressureDataList.current.length, rightFootDynamicPressureDataList.current.length);
            if (pDataLength === 0) {
                toast.error('No data available for processing.', { duration: 5000 });
                return { ok: false, message: "No dynamic pressure data yet. Ask them to walk, then confirm foot direction before saving." };
            }
            else {
                handleStopDynamic();
                setLoading(true);

                for (let i = 0; i < pDataLength; i++) {
                    const pData = new PressureData();
                    pData.ExamType = scanModeRef.current;

                    if (leftFootDynamicPressureDataList.current.length > i) {
                        pData.FrameTimes_Left = leftFootDynamicPressureDataList.current[i].FrameTimes_Left;
                        pData.CalibFrames_Left = leftFootDynamicPressureDataList.current[i].CalibFrames_Left;
                        pData.CalibMaxFrame_Left = leftFootDynamicPressureDataList.current[i].CalibMaxFrame_Left;
                        pData.ROI_Left = frameProcessor.current.autoCropROI(pData.CalibMaxFrame_Left);

                        // pData.LeftControlPoints = await footAlign.process(leftFootDynamicPressureDataList.current.at(-1).CalibMaxFrame_Left, 'left', 'dynamic');
                        // pData.Axis_Left = new Line(pData.LeftControlPoints[1], pData.LeftControlPoints[19]);
                        // pData.WidthLine_Left = new Line(pData.LeftControlPoints[3], pData.LeftControlPoints[8]);
                        // pData.HeelLine_Left = new Line(pData.LeftControlPoints[18], pData.LeftControlPoints[20]);

                        pData.COPs_Left = frameProcessor.current.GetCOPArray(pData.CalibFrames_Left, pData.ROI_Left);

                        let FootPoint_Left = frameProcessor.current.GetAllFootPoints(pData.CalibMaxFrame_Left, pData.ROI_Left);
                        const actual_Left = frameProcessor.current.convexHull(FootPoint_Left);
                        const LongestLines_Left = frameProcessor.current.Get2LongestLineSegments(frameProcessor.current.GetLineSegmentOfPolygon(actual_Left));

                        const FootPointArray_Left = frameProcessor.current.GetAllFootPoints(pData.CalibMaxFrame_Left, pData.ROI_Left);
                        const HeelPoints_Left = frameProcessor.current.GetHeelPoints(FootPointArray_Left, pData.ROI_Left);
                        const Left_HeelCenter = frameProcessor.current.FindMinimalBoundingCircle(HeelPoints_Left);

                        pData.Axis_Left = frameProcessor.current.GetHullAxisLine(pData.CalibMaxFrame_Left, pData.ROI_Left, pData.COPs_Left, LongestLines_Left, Left_HeelCenter, "Left");
                        pData.WidthLine_Left = frameProcessor.current.getWidthLine(pData.Axis_Left, pData.ROI_Left, pData.CalibMaxFrame_Left);
                        pData.HeelLine_Left = frameProcessor.current.getHeelLine(pData.Axis_Left, pData.ROI_Left, pData.CalibMaxFrame_Left);

                        pData.MetLine_Left = frameProcessor.current.GetMetatarsalLine(pData.Axis_Left, LongestLines_Left, "Left");
                        pData.Zones_Left = frameProcessor.current.GetZones(pData.MetLine_Left, pData.WidthLine_Left, pData.HeelLine_Left, pData.ROI_Left, "Left");
                    }

                    if (rightFootDynamicPressureDataList.current.length > i) {
                        pData.FrameTimes_Right = rightFootDynamicPressureDataList.current[i].FrameTimes_Right;
                        pData.CalibFrames_Right = rightFootDynamicPressureDataList.current[i].CalibFrames_Right;
                        pData.CalibMaxFrame_Right = rightFootDynamicPressureDataList.current[i].CalibMaxFrame_Right;
                        pData.ROI_Right = frameProcessor.current.autoCropROI(pData.CalibMaxFrame_Right);

                        pData.COPs_Right = frameProcessor.current.GetCOPArray(pData.CalibFrames_Right, pData.ROI_Right);

                        let FootPoint_Right = frameProcessor.current.GetAllFootPoints(pData.CalibMaxFrame_Right, pData.ROI_Right);
                        const actual_Right = frameProcessor.current.convexHull(FootPoint_Right);
                        const LongestLines_Right = frameProcessor.current.Get2LongestLineSegments(frameProcessor.current.GetLineSegmentOfPolygon(actual_Right));

                        const FootPointArray_Right = frameProcessor.current.GetAllFootPoints(pData.CalibMaxFrame_Right, pData.ROI_Right);
                        const HeelPoints_Right = frameProcessor.current.GetHeelPoints(FootPointArray_Right, pData.ROI_Right);
                        var Right_HeelCenter = frameProcessor.current.FindMinimalBoundingCircle(HeelPoints_Right);

                        pData.Axis_Right = frameProcessor.current.GetHullAxisLine(pData.CalibMaxFrame_Right, pData.ROI_Right, pData.COPs_Right, LongestLines_Right, Right_HeelCenter, "Right");
                        pData.WidthLine_Right = frameProcessor.current.getWidthLine(pData.Axis_Right, pData.ROI_Right, pData.CalibMaxFrame_Right);
                        pData.HeelLine_Right = frameProcessor.current.getHeelLine(pData.Axis_Right, pData.ROI_Right, pData.CalibMaxFrame_Right);

                        pData.MetLine_Right = frameProcessor.current.GetMetatarsalLine(pData.Axis_Right, LongestLines_Right, "Right");
                        pData.Zones_Right = frameProcessor.current.GetZones(pData.MetLine_Right, pData.WidthLine_Right, pData.HeelLine_Right, pData.ROI_Right, "Right");
                    }

                    pDataList.push(pData);
                }

                if (pDataLength > 1) {
                    pDataList[0].status = "scanned";
                } else {
                    pDataList[0].status = "done";
                }

            }

            const length_Right = Math.round(frameProcessor.current.getRealDistance(pDataList[0].Axis_Right.start, pDataList[0]?.Axis_Right.end));
            const length_Left = Math.round(frameProcessor.current.getRealDistance(pDataList[0].Axis_Left.start, pDataList[0]?.Axis_Left.end));
            pDataList[0].FootLength_Left = pDataList[0].FootLength_Right = Math.round((length_Left + length_Right) / 2) + DynamicPresureLengthConst;

            var zM1R = pDataList[0].Zones_Right.GetZoneCenteroid(ZoneNames.M1);
            var zM5R = pDataList[0].Zones_Right.GetZoneCenteroid(ZoneNames.M5);

            const zerox = pDataList[0].ROI_Right.x + pDataList[0].ROI_Right.width;
            const zeroy = pDataList[0].ROI_Right.y;

            const M1R = new cv.Point((zerox - zM1R.x) * pDataList[0].PressureSensorWidth + pDataList[0].PressureSensorWidth / 2.0, (zM1R.y - zeroy) * pDataList[0].PressureSensorHeight + pDataList[0].PressureSensorHeight / 2.0);
            const M5R = new cv.Point((zerox - zM5R.x) * pDataList[0].PressureSensorWidth + pDataList[0].PressureSensorWidth / 2.0, (zM5R.y - zeroy) * pDataList[0].PressureSensorHeight + pDataList[0].PressureSensorHeight / 2.0);

            const metCenterR = new cv.Point((M1R.x + M5R.x) / 2, (M1R.y + M5R.y) / 2);

            const ZoneSensorsR = pDataList[0].Zones_Right?.GetZoneSensors(ZoneNames.HL, pDataList[0].ROI_Right);
            if (ZoneSensorsR) {
                const hmSensorsR = pDataList[0].Zones_Right.GetZoneSensors(ZoneNames.HM, pDataList[0].ROI_Right);
                if (hmSensorsR) ZoneSensorsR.push(...hmSensorsR);
            }
            const hR = frameProcessor.current?.GetCenterOfPressure(ZoneSensorsR, pDataList[0].CalibMaxFrame_Right, 0.5);
            const heelCenterR = new cv.Point((zerox - hR.x) * pDataList[0].PressureSensorWidth + pDataList[0].PressureSensorWidth / 2.0, (hR.y - zeroy) * pDataList[0].PressureSensorHeight + pDataList[0].PressureSensorHeight / 2.0);

            var zM1L = pDataList[0].Zones_Left.GetZoneCenteroid(ZoneNames.M1);
            var zM5L = pDataList[0].Zones_Left.GetZoneCenteroid(ZoneNames.M5);

            const zeroxL = pDataList[0].ROI_Left.x + pDataList[0].ROI_Left.width;
            const zeroyL = pDataList[0].ROI_Left.y;

            const M1L = new cv.Point((zeroxL - zM1L.x) * pDataList[0].PressureSensorWidth + pDataList[0].PressureSensorWidth / 2.0, (zM1L.y - zeroyL) * pDataList[0].PressureSensorHeight + pDataList[0].PressureSensorHeight / 2.0);
            const M5L = new cv.Point((zeroxL - zM5L.x) * pDataList[0].PressureSensorWidth + pDataList[0].PressureSensorWidth / 2.0, (zM5L.y - zeroyL) * pDataList[0].PressureSensorHeight + pDataList[0].PressureSensorHeight / 2.0);

            const metCenterL = new cv.Point((M1L.x + M5L.x) / 2, (M1L.y + M5L.y) / 2);

            const ZoneSensorsL = pDataList[0].Zones_Left?.GetZoneSensors(ZoneNames.HL, pDataList[0].ROI_Left);
            if (ZoneSensorsL) {
                const hmSensorsL = pDataList[0].Zones_Left.GetZoneSensors(ZoneNames.HM, pDataList[0].ROI_Left);
                if (hmSensorsL) ZoneSensorsL.push(...hmSensorsL);
            }
            const hL = frameProcessor.current?.GetCenterOfPressure(ZoneSensorsL, pDataList[0].CalibMaxFrame_Left, 0.5);
            const heelCenterL = new cv.Point((zeroxL - hL.x) * pDataList[0].PressureSensorWidth + pDataList[0].PressureSensorWidth / 2.0, (hL.y - zeroyL) * pDataList[0].PressureSensorHeight + pDataList[0].PressureSensorHeight / 2.0);

            pDataList[0].MetCenter = [metCenterR, metCenterL];
            pDataList[0].HeelCenter = [heelCenterR, heelCenterL];
            // pDataList[0].HeelCenter = [heelCenterR, heelCenterL, new cv.Point(hR.x - pDataList[0].ROI_Right.x,hR.y - zeroy), new cv.Point(hL.x - pDataList[0].ROI_Left.x,hL.y - zeroyL)];
            // pDataList[0].HeelCenter = [heelCenterR, heelCenterL, new cv.Point(pDataList[0].ROI_Right.width - hR.x, hR.y - zeroy), new cv.Point(pDataList[0].ROI_Left.width -hL.x, hL.y - zeroyL)];

            pDataList[0].pressureDataList = pDataList.slice();
            await pDataList[0].Save();

            const event = new CustomEvent('newPressureScan', { detail: { pressureData: pDataList[0] } });
            document.dispatchEvent(event);
            onAccept();
            return { ok: true, message: "Dynamic pressure scan saved." };
        }

        return { ok: false, message: "Could not save the pressure scan." };
    }

    useEffect(() => {
        if (triggerSave) {
            void handleAccept();
        }
    }, [triggerSave]);

    const voiceActionRef = useRef<(action: string, mode?: string) => Promise<void>>(async () => undefined);
    voiceActionRef.current = async (action: string, mode?: string) => {
        const emit = (detail: Record<string, unknown>) => {
            document.dispatchEvent(new CustomEvent(VOICE_PRESSURE_SCAN_DONE_EVENT, { detail }));
        };
        const status = (message: string, extra: Record<string, unknown> = {}) => ({
            connected: connectionStatus.current,
            portPicker: connectReasonRef.current === "needsPort" || portPicker,
            noCalibration: connectReasonRef.current === "noCalibration",
            noSerial: connectReasonRef.current === "noSerial",
            mode: scanMode,
            capturing: captureDynamic,
            hasStaticCapture: Boolean(staticCaptureData),
            dynamicLeft: leftFootDynamicPressureDataList.current.length,
            dynamicRight: rightFootDynamicPressureDataList.current.length,
            canSave: scanMode === "static"
                ? Boolean(staticCaptureData)
                : leftFootDynamicPressureDataList.current.length > 0 && rightFootDynamicPressureDataList.current.length > 0,
            message,
            ...extra,
        });

        if (!connectionStatus.current && action !== "status") {
            emit(status(
                connectReasonRef.current === "noCalibration"
                    ? "Pressure is not calibrated. Tell the user they must add a pressure calibration first. Do not continue the pressure test."
                    : connectReasonRef.current === "noSerial"
                    ? "This browser cannot open the serial port. Tell them to use Chrome or Edge."
                    : "The pressure device is not connected. Tell the user to plug in the scanner, then click Connect scanner in the panel and choose the USB port. Voice cannot open that Chrome picker. After they connect, call setPressureScan action=status."
            ));
            return;
        }

        if (action === "status") {
            emit(status(
                connectionStatus.current
                    ? `Pressure scanner is connected. Mode is ${scanMode}.${captureDynamic ? " Scanning is in progress." : ""} Ask whether they want a static or a dynamic test if they have not chosen yet.`
                    : connectReasonRef.current === "noCalibration"
                    ? "Pressure is not calibrated. Tell the user they must add a pressure calibration first. Do not continue the pressure test."
                    : connectReasonRef.current === "noSerial"
                    ? "This browser cannot open the serial port. Tell them to use Chrome or Edge."
                    : "The pressure device is not connected. Tell the user to plug in the scanner, then click Connect scanner in the panel and choose the USB port. Voice cannot open that Chrome picker. After they connect, call setPressureScan action=status."
            ));
            return;
        }

        if (action === "mode") {
            if (mode === "static") {
                if (captureDynamic) handleStopDynamic();
                scanModeRef.current = "static";
                setScanMode("static");
                emit(status("Static tab selected. Ask them to stand on the sensor, then call setPressureScan action=capture when they say capture.", { mode: "static" }));
                return;
            }
            if (mode === "dynamic") {
                scanModeRef.current = "dynamic";
                setScanMode("dynamic");
                handleAutoDynamic();
                emit(status("Dynamic tab selected and scanning started. Tell them to check that the foot direction is correct. If they confirm, call setPressureScan action=save.", { mode: "dynamic", capturing: true }));
                return;
            }
            emit(status("Use mode=static or mode=dynamic."));
            return;
        }

        if (action === "capture") {
            if (scanModeRef.current !== "static") {
                scanModeRef.current = "static";
                setScanMode("static");
            }
            const result = handleCapture();
            emit(status(result.message, { hasStaticCapture: result.ok, canSave: result.ok }));
            return;
        }

        if (action === "start") {
            scanModeRef.current = "dynamic";
            setScanMode("dynamic");
            handleAutoDynamic();
            emit(status("Dynamic scanning started. Tell them to check that the foot direction is correct. If they confirm, call setPressureScan action=save.", { mode: "dynamic", capturing: true }));
            return;
        }

        if (action === "stop") {
            handleStopDynamic();
            emit(status("Dynamic scanning stopped."));
            return;
        }

        if (action === "save") {
            const result = await handleAccept();
            emit(status(result.message, { ok: result.ok, canSave: !result.ok }));
            return;
        }

        emit(status("Unknown pressure-scan action. Use status, mode, capture, start, stop, or save."));
    };

    useEffect(() => {
        const onVoiceAction = (event: Event) => {
            const detail = (event as CustomEvent).detail || {};
            void voiceActionRef.current(String(detail.action || "status"), detail.mode);
        };
        document.addEventListener(VOICE_PRESSURE_SCAN_EVENT, onVoiceAction as EventListener);
        return () => document.removeEventListener(VOICE_PRESSURE_SCAN_EVENT, onVoiceAction as EventListener);
    }, []);

    const checkRegisterOutput = (pressureData: PressureData) => {
        if (pressureData.Axis_Left.len < pressureData.ROI_Left.height * 0.5
            || pressureData.WidthLine_Left.len < pressureData.ROI_Left.width * 0.5
            // || pressureData.LeftControlPoints
        ) {
            console.log('!!!!!!!!!!');
            SetDefaultControlPoints(pressureData, 'left');
        }

        if (pressureData.Axis_Right.len < pressureData.ROI_Right.height * 0.5
            || pressureData.WidthLine_Right.len < pressureData.ROI_Right.width * 0.5
            // || pressureData.LeftControlPoints
        ) {
            console.log('+++++++++');
            SetDefaultControlPoints(pressureData, 'right');
        }
    }

    const SetDefaultControlPoints = (pressureData: PressureData, foot: string) => {
        if (!pressureData) return;

        if (pressureData.ExamType == 'dynamic') {
            if (foot == 'left') {
                pressureData.COPs_Left = frameProcessor.current.GetCOPArray(pressureData.CalibFrames_Left, pressureData.ROI_Left);

                let FootPoint_Left = frameProcessor.current.GetAllFootPoints(pressureData.CalibMaxFrame_Left, pressureData.ROI_Left);
                const actual_Left = frameProcessor.current.convexHull(FootPoint_Left);
                const LongestLines_Left = frameProcessor.current.Get2LongestLineSegments(frameProcessor.current.GetLineSegmentOfPolygon(actual_Left));

                const FootPointArray_Left = frameProcessor.current.GetAllFootPoints(pressureData.CalibMaxFrame_Left, pressureData.ROI_Left);
                const HeelPoints_Left = frameProcessor.current.GetHeelPoints(FootPointArray_Left, pressureData.ROI_Left);
                const Left_HeelCenter = frameProcessor.current.FindMinimalBoundingCircle(HeelPoints_Left);

                pressureData.Axis_Left = frameProcessor.current.GetHullAxisLine(pressureData.CalibMaxFrame_Left, pressureData.ROI_Left, pressureData.COPs_Left, LongestLines_Left, Left_HeelCenter, "Left");
                pressureData.WidthLine_Left = frameProcessor.current.getWidthLine(pressureData.Axis_Left, pressureData.ROI_Left, pressureData.CalibMaxFrame_Left);
                pressureData.HeelLine_Left = frameProcessor.current.getHeelLine(pressureData.Axis_Left, pressureData.ROI_Left, pressureData.CalibMaxFrame_Left);

                pressureData.MetLine_Left = frameProcessor.current.GetMetatarsalLine(pressureData.Axis_Left, LongestLines_Left, "Left");
                pressureData.Zones_Left = frameProcessor.current.GetZones(pressureData.MetLine_Left, pressureData.WidthLine_Left, pressureData.HeelLine_Left, pressureData.ROI_Left, "Left");
            }
            else {
                pressureData.COPs_Right = frameProcessor.current.GetCOPArray(pressureData.CalibFrames_Right, pressureData.ROI_Right);

                let FootPoint_Right = frameProcessor.current.GetAllFootPoints(pressureData.CalibMaxFrame_Right, pressureData.ROI_Right);
                const actual_Right = frameProcessor.current.convexHull(FootPoint_Right);
                const LongestLines_Right = frameProcessor.current.Get2LongestLineSegments(frameProcessor.current.GetLineSegmentOfPolygon(actual_Right));

                const FootPointArray_Right = frameProcessor.current.GetAllFootPoints(pressureData.CalibMaxFrame_Right, pressureData.ROI_Right);
                const HeelPoints_Right = frameProcessor.current.GetHeelPoints(FootPointArray_Right, pressureData.ROI_Right);
                var Right_HeelCenter = frameProcessor.current.FindMinimalBoundingCircle(HeelPoints_Right);

                pressureData.Axis_Right = frameProcessor.current.GetHullAxisLine(pressureData.CalibMaxFrame_Right, pressureData.ROI_Right, pressureData.COPs_Right, LongestLines_Right, Right_HeelCenter, "Right");
                pressureData.WidthLine_Right = frameProcessor.current.getWidthLine(pressureData.Axis_Right, pressureData.ROI_Right, pressureData.CalibMaxFrame_Right);
                pressureData.HeelLine_Right = frameProcessor.current.getHeelLine(pressureData.Axis_Right, pressureData.ROI_Right, pressureData.CalibMaxFrame_Right);

                pressureData.MetLine_Right = frameProcessor.current.GetMetatarsalLine(pressureData.Axis_Right, LongestLines_Right, "Right");
                pressureData.Zones_Right = frameProcessor.current.GetZones(pressureData.MetLine_Right, pressureData.WidthLine_Right, pressureData.HeelLine_Right, pressureData.ROI_Right, "Right");
            }
        }
        else if (pressureData.ExamType == 'static') {
            if (foot == 'left') {
                pressureData.COPs_Left = frameProcessor.current.GetCOPArray(pressureData.CalibFrames_Left, pressureData.ROI_Left);

                const leftROI = pressureData.ROI_Left;
                const left_TOPROI = new cv.Rect(leftROI.x, leftROI.y, leftROI.width, leftROI.height / 3.0);
                const left_BottomROI = new cv.Rect(leftROI.x, leftROI.y + (leftROI.height * 0.66), leftROI.width, leftROI.height / 3.0);

                var HelperCOPs_Left: cv.Point[] = [];
                HelperCOPs_Left.push(frameProcessor.current.GetCOPArray(pressureData.CalibFrames_Left, left_BottomROI)[0]);
                HelperCOPs_Left.push(frameProcessor.current.GetCOPArray(pressureData.CalibFrames_Left, left_TOPROI)[0]);

                pressureData.Axis_Left = frameProcessor.current.getAxisLine(HelperCOPs_Left, pressureData.ROI_Left, pressureData.CalibMaxFrame_Left);
                pressureData.WidthLine_Left = frameProcessor.current.getWidthLine(pressureData.Axis_Left, pressureData.ROI_Left, pressureData.CalibMaxFrame_Left);
                pressureData.HeelLine_Left = frameProcessor.current.GetHeelLine(pressureData.Axis_Left, pressureData.ROI_Left, pressureData.CalibMaxFrame_Left);

                pressureData.Zones_Left = frameProcessor.current.GetZones(pressureData.Axis_Left, pressureData.WidthLine_Left, pressureData.HeelLine_Left, pressureData.ROI_Left, "Left");
            }
            else {
                pressureData.COPs_Right = frameProcessor.current.GetCOPArray(pressureData.CalibFrames_Right, pressureData.ROI_Right);

                const rightROI = pressureData.ROI_Right;
                const right_TOPROI = new cv.Rect(rightROI.x, rightROI.y, rightROI.width, rightROI.height / 3.0);
                const right_BottomROI = new cv.Rect(rightROI.x, rightROI.y + (rightROI.height * 0.66), rightROI.width, rightROI.height / 3.0);

                var HelperCOPs_Right: cv.Point[] = [];
                HelperCOPs_Right.push(frameProcessor.current.GetCOPArray(pressureData.CalibFrames_Right, right_BottomROI)[0]);
                HelperCOPs_Right.push(frameProcessor.current.GetCOPArray(pressureData.CalibFrames_Right, right_TOPROI)[0]);

                pressureData.Axis_Right = frameProcessor.current.getAxisLine(HelperCOPs_Right, pressureData.ROI_Right, pressureData.CalibMaxFrame_Right);
                pressureData.WidthLine_Right = frameProcessor.current.getWidthLine(pressureData.Axis_Right, pressureData.ROI_Right, pressureData.CalibMaxFrame_Right);
                pressureData.HeelLine_Right = frameProcessor.current.GetHeelLine(pressureData.Axis_Right, pressureData.ROI_Right, pressureData.CalibMaxFrame_Right);

                pressureData.Zones_Right = frameProcessor.current.GetZones(pressureData.Axis_Right, pressureData.WidthLine_Right, pressureData.HeelLine_Right, pressureData.ROI_Right, "Right");
            }
        }

        const length_Right = Math.round(frameProcessor.current.getRealDistance(pressureData.Axis_Right.start, pressureData?.Axis_Right.end));
        const length_Left = Math.round(frameProcessor.current.getRealDistance(pressureData.Axis_Left.start, pressureData?.Axis_Left.end));
        pressureData.FootLength_Left = pressureData.FootLength_Right = (length_Left + length_Right) / 2;
    }

    const exportBitmap = (mat?: Mat, filename: string = "pressure_scan.png") => {
        if (!mat) {
            toast.error("No image data to export.", { duration: 5000 });
            return;
        }
        try {
            // Convert Mat to canvas
            const canvas = document.createElement("canvas");
            canvas.width = mat.cols;
            canvas.height = mat.rows;
            cv.imshow(canvas, mat);

            // Create a link and trigger download
            canvas.toBlob((blob) => {
                if (!blob) {
                    toast.error("Failed to export bitmap.", { duration: 5000 });
                    return;
                }
                const link = document.createElement("a");
                link.href = URL.createObjectURL(blob);
                link.download = filename;
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                URL.revokeObjectURL(link.href);
            }, "image/png");
        } catch (err) {
            toast.error("Error exporting bitmap.", { duration: 5000 });
            console.error("Export bitmap error:", err);
        }
    };

    const handleDynamicLeft = () => {
        setDynamicTest('auto');
        setCaptureDynamic(true);
        startCapture(false);
    }

    const handleDynamicRight = () => {
        setDynamicTest('right');
        setCaptureDynamic(true);
        startCapture(false);
    }

    const handleAutoDynamic = () => {
        setDynamicTest('auto');
        setCaptureDynamic(true);
        startCapture(false);
    }

    const handleStopDynamic = () => {
        setCaptureDynamic(false);
        setDynamicTest('none');
        startCapture(true);
    }

    const handleToggleDynamic = () => {
        if (captureDynamic) {
            handleStopDynamic();
        } else {
            handleAutoDynamic();
        }
    }

    const startCapture = (stopScanner: boolean) => {
        if (!stopScanner) {
            footPressed.current = false;
            stopTimer.current = 0;
            scannerRef.current?.record(60.0);
        }
        else {
            scannerRef.current?.stopRecord();
            setDynamicTest('none');
        }
    }

    const connect = async () => {
        try {
            if (!("serial" in navigator)) {
                connectReasonRef.current = "noSerial";
                connectionStatus.current = false;
                setPortPicker(false);
                setLoading(false);
                toast.error("Web Serial is only available in Chrome or Edge.", { duration: 10000 });
                return;
            }
            if (user?.id == null || user.id < 0) {
                throw new Error("User is not logged in");
            }
            const calibrator = await getPressureCalib(api, user.id);
            if (calibrator == null) {
                connectReasonRef.current = "noCalibration";
                connectionStatus.current = false;
                setPortPicker(false);
                setLoading(false);
                toast.error("your device calibration files is missing", { duration: 10000 });
                return;
            }
            scannerRef.current?.setCalibrator(calibrator);
            await scannerRef.current?.Connect();
            connectReasonRef.current = "ok";
            connectionStatus.current = true;
            setPortPicker(false);
        } catch (err: any) {
            console.error("Error connecting:", err);
            connectReasonRef.current = "needsPort";
            connectionStatus.current = false;
            setPortPicker(true);
            setLoading(false);
            if (!scannerRef.current?.isCanceled) {
                toast.error(err.message || "Failed to connect to the device", { duration: 10000 });
            }
        }
    }

    const onPickSerialPort = async () => {
        try {
            setLoading(true);
            await scannerRef.current?.pickPort();
            await connect();
            if (connectionStatus.current) startDataLoop();
            document.dispatchEvent(new CustomEvent(VOICE_PRESSURE_SCAN_READY_EVENT, {
                detail: {
                    connected: connectionStatus.current,
                    portPicker: !connectionStatus.current,
                    mode: scanMode,
                    capturing: false,
                    message: connectionStatus.current
                        ? "Pressure scanner is connected. Ask whether they want a static or a dynamic test."
                        : "The pressure device is still not connected. Ask them to plug it in and click Connect scanner again.",
                }
            }));
        } catch (err: any) {
            setLoading(false);
            setPortPicker(true);
            if (err?.name === "NotFoundError") return;
            toast.error(err?.message || "Could not open the serial port", { duration: 10000 });
        }
    }

    const disconnect = async () => {
        try {
            stopDataLoop();
            await scannerRef.current?.Disconnect();
            connectionStatus.current = false;
        } catch (err) {
            console.error("Error disconnecting: ", err);
        }
    }

    const checkAcceptEnable = () => {
        if (rightFootDynamicPressureDataList.current.length > 0 && leftFootDynamicPressureDataList.current.length > 0) {
            setIsAcceptEnabled(true);
        } else {
            setIsAcceptEnabled(false);
        }
    };

    const deleteFromGallery = (index: number, isLeft: boolean) => {
        if (isLeft) {
            leftGallery.splice(index, 1);
            leftFootDynamicPressureDataList.current.splice(index, 1);
            setLeftGallery([...leftGallery]);
        } else {
            rightGallery.splice(index, 1);
            rightFootDynamicPressureDataList.current.splice(index, 1);
            setRightGallery([...rightGallery]);
        }
        checkAcceptEnable();
    };

    const replaceToGallery = (index: number, isLeft: boolean) => {
        if (isLeft) {
            const newItem = leftGallery[index];
            setRightGallery([...rightGallery, newItem]);
            leftGallery.splice(index, 1);
            setLeftGallery([...leftGallery]);

            leftFootDynamicPressureDataList.current[index].CalibFrames_Right = (leftFootDynamicPressureDataList.current[index].CalibFrames_Left.splice(0));
            leftFootDynamicPressureDataList.current[index].FrameTimes_Right = (leftFootDynamicPressureDataList.current[index].FrameTimes_Left.splice(0));
            leftFootDynamicPressureDataList.current[index].CalibMaxFrame_Right = leftFootDynamicPressureDataList.current[index].CalibMaxFrame_Left.clone();
            rightFootDynamicPressureDataList.current.push(leftFootDynamicPressureDataList.current[index]);
            leftFootDynamicPressureDataList.current.splice(index, 1);
        } else {
            const newItem = rightGallery[index];
            setLeftGallery([...leftGallery, newItem]);
            rightGallery.splice(index, 1);
            setRightGallery([...rightGallery]);

            rightFootDynamicPressureDataList.current[index].CalibFrames_Left = (rightFootDynamicPressureDataList.current[index].CalibFrames_Right.splice(0));
            rightFootDynamicPressureDataList.current[index].FrameTimes_Left = (rightFootDynamicPressureDataList.current[index].FrameTimes_Right.splice(0));
            rightFootDynamicPressureDataList.current[index].CalibMaxFrame_Left = rightFootDynamicPressureDataList.current[index].CalibMaxFrame_Right.clone();
            leftFootDynamicPressureDataList.current.push(rightFootDynamicPressureDataList.current[index]);
            rightFootDynamicPressureDataList.current.splice(index, 1);
        }
        checkAcceptEnable();
    };

    const getToggleButtonStyle = (isActive: boolean, isHovered: boolean, disabled: boolean = false): React.CSSProperties => ({
        backgroundColor: isActive || isHovered ? '#076596' : 'white',
        color: isActive || isHovered ? 'white' : '#076596',
        cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? '0.6' : '1',
        pointerEvents: disabled ? 'none' : 'auto',
    })

    return (
        <>
            {loading ? (
                <Box sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    alignSelf: 'center',
                    height: '100%',
                    minWidth: 0,
                    flex: 1,
                    color: 'var(--accent-color)'
                }}>
                    <CircularProgress color="inherit" />
                </Box>
            ) : portPicker ? (
                <Box sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    alignItems: 'center',
                    alignSelf: 'center',
                    gap: 2,
                    height: '100%',
                    minWidth: 0,
                    flex: 1,
                    px: 3,
                    textAlign: 'center',
                    color: 'var(--text-color)'
                }}>
                    <Box sx={{ maxWidth: 420, opacity: 0.85 }}>
                        Chrome must open the serial port from a click. Connect the pressure scanner, then choose it in the browser list.
                    </Box>
                    <button
                        type="button"
                        onClick={onPickSerialPort}
                        style={{
                            backgroundColor: '#076596',
                            color: 'white',
                            border: 'none',
                            borderRadius: 8,
                            padding: '10px 18px',
                            cursor: 'pointer',
                            fontWeight: 600,
                        }}
                    >
                        Connect scanner
                    </button>
                </Box>
            ) : (
                <div className="pressure-scan-container">
                    <div style={{ flex: 1, minWidth: 0, height: '100%' }}>
                        <div className="left-container">
                            <PressureViewerComponent mode="online" imgData={newScannerData} />

                            {/* <Box sx={{ display: 'flex', flexDirection: 'row' }}>
                                <label id="bufferRead" >Sum Frame: {sumFrame}</label>
                                <label id="bufferProcess" > Saved Frames: {savedFrame}</label>
                                <label id="bufferProcess" > Frame Rate: {frameRate}</label>
                            </Box> */}
                        </div>
                    </div>
                    <div style={{ flex: 1, minWidth: 0, height: '100%', display: 'flex', flexDirection: 'column' }}>
                        <div className="pressure-toolbar">
                            <div className="tab-mode-container">
                                <div className="tab-button-container">
                                    <button
                                        disabled={captureDynamic}
                                        style={getToggleButtonStyle(scanMode === 'static', hovered === 'static', captureDynamic)}
                                        onClick={() => {
                                            if (scanMode !== 'static') {
                                                setScanMode('static')
                                            }
                                        }}
                                        onMouseEnter={() => setHoverd('static')}
                                        onMouseLeave={() => setHoverd(null)}
                                        className={scanMode === 'static' ? 'active' : ''}>
                                        <svg style={{ width: '24px', height: '24px' }} viewBox="0 0 24 24">
                                            <path fill="currentColor" d="M12 2C11.17 2 10.5 2.67 10.5 3.5C10.5 4.33 11.17 5 12 5C12.83 5 13.5 4.33 13.5 3.5C13.5 2.67 12.83 2 12 2ZM15 22V13H17V22H15ZM7 22V11H9V22H7ZM11 22V8H13V22H11Z" />
                                        </svg>
                                        <span style={{ fontSize: '12px' }}>
                                            Static
                                        </span>
                                    </button>
                                </div>

                                <div className="tab-button-container">
                                    <button
                                        disabled={captureDynamic}
                                        style={getToggleButtonStyle(scanMode === 'dynamic', hovered === 'dynamic', captureDynamic)}
                                        onClick={() => {
                                            if (scanMode !== 'dynamic') {
                                                setScanMode('dynamic');
                                            }
                                            handleAutoDynamic();
                                            checkAcceptEnable();
                                        }}
                                        onMouseEnter={() => setHoverd('dynamic')}
                                        onMouseLeave={() => setHoverd(null)}
                                        className={scanMode === 'dynamic' ? 'active' : ''}>
                                        <svg style={{ width: '24px', height: '24px' }} viewBox="0 0 24 24">
                                            <path fill="currentColor" d="M13.5,5.5C14.59,5.5 15.5,4.58 15.5,3.5C15.5,2.38 14.59,1.5 13.5,1.5C12.39,1.5 11.5,2.38 11.5,3.5C11.5,4.58 12.39,5.5 13.5,5.5M9.89,19.38L10.89,15L13,17V23H15V15.5L12.89,13.5L13.5,10.5C14.79,12 16.79,13 19,13V11C17.09,11 15.5,10 14.69,8.58L13.69,7C13.29,6.38 12.69,6 12,6C11.69,6 11.5,6.1 11.31,6.19L6,8.28V13H8V9.58L9.79,8.88L8.19,17L3.29,16L2.84,18L9.89,19.38Z" />
                                        </svg>
                                        <span style={{ fontSize: '12px' }}>
                                            Dynamic
                                        </span>
                                    </button>
                                </div>
                            </div>

                            <div className="mode-container">
                                {scanMode === 'static' ? (
                                    <div className="button-container">
                                        <button
                                            style={getToggleButtonStyle(false, hovered === 'capture')}
                                            onMouseEnter={() => setHoverd('capture')}
                                            onMouseLeave={() => setHoverd(null)}
                                            onClick={handleCapture}>
                                            <CameraOutlined />
                                        </button>
                                        <span>Capture</span>
                                    </div>
                                ) : (
                                    <div className="button-container">
                                        <button
                                            style={getToggleButtonStyle(false, hovered === 'stop')}
                                            onMouseEnter={() => setHoverd('stop')}
                                            onMouseLeave={() => setHoverd(null)}
                                            onClick={handleToggleDynamic}
                                        >
                                            {captureDynamic ? <StopOutlined /> : <PlayArrowOutlined />}
                                        </button>
                                        <span>{captureDynamic ? 'Stop' : 'Start'} Scanning</span>
                                    </div>
                                )}
                                {/* <div className="button-container">
                                    <button
                                        disabled={!isAcceptEnabled}
                                        style={getToggleButtonStyle(false, hovered === 'accept', !isAcceptEnabled)}
                                        onMouseEnter={() => setHoverd('accept')}
                                        onMouseLeave={() => setHoverd(null)}
                                        onClick={handleAccept}>
                                        <SaveOutlined />
                                    </button>
                                    <span>Save</span>
                                </div> */}
                            </div>

                        </div>
                        {scanMode === 'static' ? (
                            <div className="right-container">
                                <PressureViewerComponent mode="viewer" imgData={staticCaptureData} />
                            </div>
                        ) : (
                            // dynamicTest !== 'auto' ? (
                            //     <div className="right-container">
                            //         <div className="dynamic-viewer-container">
                            //             <PressureViewerComponent mode="viewer" imgData={leftDynamicData} />
                            //         </div>
                            //         <div className="dynamic-viewer-container">
                            //             <PressureViewerComponent mode="viewer" imgData={rightDynamicData} />
                            //         </div>
                            //     </div>
                            // ) : (
                            <div className="right-container">
                                <Box sx={{ display: 'flex', flexDirection: 'row', gap: '8px', width: '100%', height: '100%', overflow: 'hidden' }}>
                                    <ImageList className="dynamic-pressure-gallery" cols={1} gap={8} rowHeight={134}>
                                        {leftGallery.map((item, index) => (
                                            <ImageListItem key={item} sx={{
                                                '&:hover': { backgroundColor: "#b0d3f0" },
                                                display: 'flex',
                                                flexDirection: 'row',
                                                justifyContent: 'space-between',
                                                width: '100%',
                                            }}>
                                                <IconButton
                                                    size="large"
                                                    onClick={() => deleteFromGallery(index, true)}
                                                    aria-label="Delete"
                                                    title="Delete"
                                                    style={{ flex: 1, borderRadius: '0', color: '#ff5677' }}
                                                >
                                                    <DeleteIcon />
                                                </IconButton>
                                                <img className="dynamic-pressure-image" style={{ width: '88px', height: '104px', flexGrow: 0 }}
                                                    src={item}
                                                    loading="lazy"
                                                />
                                                <IconButton
                                                    size="medium"
                                                    onClick={() => replaceToGallery(index, true)}
                                                    aria-label="Send to Right"
                                                    title="Send to Right"
                                                    style={{ flex: 1, borderRadius: '0' }}
                                                >
                                                    <CopyToRightIcon />
                                                </IconButton>
                                            </ImageListItem>
                                        ))}
                                    </ImageList>
                                    <ImageList className="dynamic-pressure-gallery" cols={1} gap={8} rowHeight={134}>
                                        {rightGallery.map((item, index) => (
                                            <ImageListItem key={item} sx={{
                                                '&:hover': { backgroundColor: "#b0d3f0" },
                                                display: 'flex',
                                                flexDirection: 'row',
                                                justifyContent: 'space-between',
                                                width: '100%',
                                            }}>
                                                <IconButton
                                                    onClick={() => replaceToGallery(index, false)}
                                                    aria-label="Send to Left"
                                                    title="Send to Left"
                                                    style={{ flex: 1, borderRadius: '0' }}
                                                >
                                                    <CopyToLeftIcon />
                                                </IconButton>
                                                <img className="dynamic-pressure-image" style={{ width: '88px', height: '104px', flexGrow: 0 }}
                                                    src={item}
                                                    loading="lazy"
                                                />
                                                <IconButton
                                                    onClick={() => deleteFromGallery(index, false)}
                                                    aria-label="Delete"
                                                    title="Delete"
                                                    style={{ flex: 1, borderRadius: '0', color: '#ff5677' }}
                                                >
                                                    <DeleteIcon />
                                                </IconButton>
                                            </ImageListItem>
                                        ))}
                                    </ImageList>
                                </Box>
                            </div>
                            // ) 
                        )}
                    </div>
                    <div style={{ display: 'none' }}>
                        <PressureViewerComponent mode="viewer" imgData={null} ref={pressureViewerRef} />
                    </div>
                </div>
            )}
        </>
    );
};

export default NewScan;